import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Row, Col, message, Modal, Typography, Button, Divider } from 'antd';
import { EditOutlined, CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import apiService, { Lead, User } from '../../services/api';
import { useApp } from '../../contexts/AppContext';
import { useCognitoAuth } from '../../contexts/CognitoAuthContext';
import { getUserIdAsNumber } from '../../utils/cognitoUtils';
import { isAdmin } from '../../utils/roleUtils';
import LeadList, { WorkspaceFilters } from './LeadList';
import LeadHeader, { LeadCardTitle, type LeadHeaderHandle } from './LeadHeader';
import Workboard from './Workboard';
import DocumentsHub from './DocumentsHub';
import ActivityRail from './ActivityRail';
import NewLeadForm from './NewLeadForm';
import ClientManagement from '../ClientManagement';
import { workspacePage, workspaceSurface, workspaceCardHead, workspaceCardBody } from './workspaceTheme';

const { Text } = Typography;

const DEFAULT_FILTERS: WorkspaceFilters = {
  search: '',
  section: 'all',
  area: 'all',
};

const ClientWorkspace: React.FC = () => {
  const { user } = useCognitoAuth();
  const { currentBusinessArea } = useApp();
  const location = useLocation();
  const navigate = useNavigate();

  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [filters, setFilters] = useState<WorkspaceFilters>(DEFAULT_FILTERS);
  const [createOpen, setCreateOpen] = useState(false);
  const [fichaExpanded, setFichaExpanded] = useState(false);
  const leadEditRef = useRef<LeadHeaderHandle>(null);
  const leadsRef = useRef<Lead[]>([]);
  leadsRef.current = leads;

  const isAdminUser = Boolean(user?.role && isAdmin(user.role));
  const [viewAsUserId, setViewAsUserId] = useState<number | null>(null);
  const [viewAsUsers, setViewAsUsers] = useState<User[]>([]);
  const [loadingViewAsUsers, setLoadingViewAsUsers] = useState(false);

  const resolveLeadsUserId = useCallback((): number | undefined => {
    if (!user) return undefined;
    if (isAdminUser && viewAsUserId != null) {
      return viewAsUserId;
    }
    return getUserIdAsNumber(user);
  }, [user, isAdminUser, viewAsUserId]);

  const fetchLeads = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await apiService.getLeads(currentBusinessArea, resolveLeadsUserId());
      const sorted = [...data].sort(
        (a, b) =>
          new Date(b.updated_at || b.created_at).getTime() -
          new Date(a.updated_at || a.created_at).getTime()
      );
      setLeads(sorted);
      return sorted;
    } catch {
      message.error('Erro ao carregar leads.');
    } finally {
      setLoading(false);
    }
  }, [user, currentBusinessArea, resolveLeadsUserId]);

  useEffect(() => {
    if (!isAdminUser || !user) {
      setViewAsUsers([]);
      setViewAsUserId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingViewAsUsers(true);
      try {
        const users = await apiService.getViewableUsers();
        if (cancelled) return;
        setViewAsUsers(users);
        const selfId = getUserIdAsNumber(user);
        setViewAsUserId((prev) => {
          if (prev != null && users.some((u) => u.id === prev)) return prev;
          return selfId;
        });
      } catch {
        if (!cancelled) message.error('Erro ao carregar utilizadores.');
      } finally {
        if (!cancelled) setLoadingViewAsUsers(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdminUser, user]);

  const handleAdminViewAsUserChange = useCallback(
    (userId: number) => {
      setViewAsUserId(userId);
      setSelectedLead(null);
      navigate('/client-workspace', { replace: true });
    },
    [navigate],
  );

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    if (selectedLead) {
      setFichaExpanded(true);
    } else {
      setFichaExpanded(false);
    }
  }, [selectedLead?.id]);

  // Honor ?leadId= only when the URL changes — do not depend on `leads` or every refetch
  // fights list selection (stale ?leadId= kept re-applying the old lead).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const leadIdParam = params.get('leadId');
    if (!leadIdParam) return;
    const id = parseInt(leadIdParam, 10);
    if (!Number.isFinite(id)) return;
    const list = leadsRef.current;
    const existing = list.find((l) => l.id === id);
    if (existing) {
      setSelectedLead(existing);
      return;
    }
    let cancelled = false;
    apiService
      .getLead(id)
      .then((l) => {
        if (!cancelled) setSelectedLead(l);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only on search; reads latest list via ref
  }, [location.search]);

  const handleSelectLead = useCallback(async (lead: Lead) => {
    setSelectedLead(lead);
    try {
      const fresh = await apiService.getLead(lead.id);
      setSelectedLead(fresh);
      setLeads((prev) => prev.map((l) => (l.id === fresh.id ? (fresh as Lead) : l)));
    } catch {
      // keep stale selection if fresh fetch fails
    }
  }, []);

  const handleLeadListSelection = useCallback(
    (lead: Lead | null) => {
      if (lead === null) {
        setSelectedLead(null);
        navigate('/client-workspace', { replace: true });
        return;
      }
      navigate(`/client-workspace?leadId=${lead.id}`, { replace: true });
      void handleSelectLead(lead);
    },
    [navigate, handleSelectLead],
  );

  const handleLeadChange = (updated: Lead) => {
    setSelectedLead(updated);
    setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
  };

  const handleLeadCreated = async (lead: Lead) => {
    setCreateOpen(false);
    await fetchLeads();
    handleSelectLead(lead);
    navigate(`/client-workspace?leadId=${lead.id}`, { replace: true });
  };

  /** Kanban "Ver detalhes" in the embedded pipeline: select in Clientes list and expand workspace ficha (no modal). */
  const handleOpenLeadFromWorkspaceKanban = useCallback(
    (lead: Lead) => {
      navigate(`/client-workspace?leadId=${lead.id}`, { replace: true });
      setFichaExpanded(true);
      void handleSelectLead(lead);
    },
    [navigate, handleSelectLead],
  );

  return (
    <div style={{ ...workspacePage, height: 'calc(100vh - 110px)' }}>
      <Row gutter={[16, 16]} style={{ height: '100%' }} wrap={false}>
        <Col flex="320px" style={{ height: '100%', minHeight: 0 }}>
          <LeadList
            leads={leads}
            loading={loading}
            selectedLeadId={selectedLead?.id || null}
            onSelectLead={handleLeadListSelection}
            onCreateLead={() => setCreateOpen(true)}
            onRefresh={fetchLeads}
            filters={filters}
            onFiltersChange={setFilters}
            adminViewAsUserId={isAdminUser ? viewAsUserId : undefined}
            adminViewAsUsers={isAdminUser ? viewAsUsers : undefined}
            onAdminViewAsUserChange={isAdminUser ? handleAdminViewAsUserChange : undefined}
            loadingAdminUsers={loadingViewAsUsers}
          />
        </Col>
        <Col
          flex="auto"
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              marginBottom: 12,
            }}
          >
            <div
              style={{
                ...workspaceSurface,
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <ClientManagement
                workspaceEmbed
                workspaceViewAsUserId={
                  isAdminUser && viewAsUserId != null ? viewAsUserId : undefined
                }
                onOpenLeadInWorkspace={handleOpenLeadFromWorkspaceKanban}
              />
            </div>
          </div>
          <div
            style={{
              flexShrink: 0,
              width: '100%',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              ...(selectedLead && fichaExpanded
                ? {
                    position: 'absolute' as const,
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                    zIndex: 10,
                  }
                : {
                    position: 'relative' as const,
                    flex: '0 0 auto',
                  }),
            }}
          >
            <div
              style={{
                ...workspaceSurface,
                marginBottom: selectedLead && fichaExpanded ? 0 : 16,
                width: '100%',
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                boxShadow:
                  selectedLead && fichaExpanded
                    ? '0 -8px 40px rgba(15, 23, 42, 0.18)'
                    : undefined,
              }}
            >
              <div
                style={{
                  flex: selectedLead && fichaExpanded ? '1 1 0%' : '0 0 0px',
                  minHeight: 0,
                  overflow: selectedLead && fichaExpanded ? 'auto' : 'hidden',
                  opacity: selectedLead && fichaExpanded ? 1 : 0,
                  transition: 'opacity 0.22s ease',
                  pointerEvents: selectedLead && fichaExpanded ? 'auto' : 'none',
                  ...workspaceCardBody,
                  borderTopLeftRadius: 14,
                  borderTopRightRadius: 14,
                }}
              >
                {selectedLead && fichaExpanded && (
                  <Row gutter={[16, 16]} style={{ margin: 0, alignItems: 'flex-start' }}>
                    <Col xs={24} xl={16}>
                      <LeadHeader
                        ref={leadEditRef}
                        lead={selectedLead}
                        onLeadChange={handleLeadChange}
                        embedded
                      />
                      <Divider style={{ margin: 0 }} />
                      <Workboard
                        lead={selectedLead}
                        onLeadChange={handleLeadChange}
                        embedded
                        adminViewAsUserId={isAdminUser ? viewAsUserId : undefined}
                        adminViewAsUsers={isAdminUser ? viewAsUsers : undefined}
                        onAdminViewAsUserChange={isAdminUser ? handleAdminViewAsUserChange : undefined}
                        loadingAdminUsers={loadingViewAsUsers}
                      />
                      <Divider style={{ margin: 0 }} />
                      <DocumentsHub lead={selectedLead} embedded />
                    </Col>
                    <Col xs={24} xl={8}>
                      <ActivityRail lead={selectedLead} />
                    </Col>
                  </Row>
                )}
              </div>
              <div
                style={{
                  ...workspaceCardHead,
                  borderBottom: 'none',
                  borderTop:
                    selectedLead && fichaExpanded
                      ? '1px solid rgba(15, 23, 42, 0.08)'
                      : 'none',
                  padding: '12px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap',
                  flexShrink: 0,
                  borderBottomLeftRadius: 14,
                  borderBottomRightRadius: 14,
                }}
              >
                <div
                  role={selectedLead ? 'button' : undefined}
                  tabIndex={selectedLead ? 0 : -1}
                  aria-expanded={selectedLead ? fichaExpanded : undefined}
                  aria-disabled={!selectedLead}
                  aria-label={
                    selectedLead
                      ? fichaExpanded
                        ? 'Fechar workspace (recolher para baixo)'
                        : 'Abrir workspace (expandir para cima)'
                      : 'Selecione um cliente na lista para abrir o workspace'
                  }
                  onClick={() => {
                    if (selectedLead) setFichaExpanded((v) => !v);
                  }}
                  onKeyDown={(e) => {
                    if (!selectedLead) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setFichaExpanded((v) => !v);
                    }
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 12,
                    flexWrap: 'wrap',
                    cursor: selectedLead ? 'pointer' : 'default',
                    userSelect: 'none',
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {selectedLead ? (
                    fichaExpanded ? (
                      <CaretDownOutlined aria-hidden />
                    ) : (
                      <CaretUpOutlined aria-hidden />
                    )
                  ) : null}
                  {selectedLead ? (
                    <LeadCardTitle lead={selectedLead} />
                  ) : (
                    <span>
                      <span style={{ fontWeight: 600 }}>Workspace de Cliente</span>
                      <Text type="secondary" style={{ marginLeft: 8 }}>
                        — Selecione um cliente na lista
                      </Text>
                    </span>
                  )}
                </div>
                {selectedLead ? (
                  <Button
                    icon={<EditOutlined />}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      leadEditRef.current?.openEdit();
                    }}
                  >
                    Editar
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <Modal
        title="Novo Cliente"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        footer={null}
        width={720}
        destroyOnClose
      >
        <NewLeadForm
          defaultBusinessArea={currentBusinessArea}
          onCreated={handleLeadCreated}
          onCancel={() => setCreateOpen(false)}
        />
      </Modal>
    </div>
  );
};

export default ClientWorkspace;
